import { translate, localHref } from "../i18n";
import { useState, useEffect } from "react";
import SeasonalIdeas from "../components/SeasonalIdeas";
import CinematicHero, { LoopFilm } from "../components/CinematicHero";
import { Link } from "react-router-dom";
import { motion, useScroll, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import { PageTransition, WordReveal, ScribbleUnderline, Reveal } from "../components/transitions";
import { ArrowRight, CaretRight, List as ListIcon, X, Printer, Megaphone, StickerIcon, QrCode, Storefront, Lightning, ShieldCheck, Sparkle, Handshake, PaperPlaneTilt, MapPin, Phone, EnvelopeSimple } from "@phosphor-icons/react";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL || ""}/api`;
const NAV = [{
  id: "storia",
  label: translate("Storia")
}, {
  id: "servizi",
  label: translate("Servizi")
}, {
  id: "modello",
  label: translate("Come lavoriamo")
}, {
  id: "questo-mese",
  label: translate("Questo mese")
}, {
  id: "contatti",
  label: translate("Contatti")
}];
export const SERVICES = [{
  icon: Printer,
  title: translate("Stampati aziendali"),
  items: [translate("Biglietti da visita"), translate("Flyer e volantini"), translate("Depliant e brochure"), translate("Cataloghi e listini"), translate("Cartelline e modulistica"), translate("Locandine e manifesti")],
  tone: "light"
}, {
  icon: Megaphone,
  title: translate("Grande formato"),
  items: [translate("Banner e striscioni"), translate("Pannelli Forex, Dibond, PVC, Plexiglass"), translate("Poster e stampe promozionali"), translate("Espositori per negozi ed eventi"), translate("Segnaletica interna ed esterna"), translate("Vetrofanie e adesivi vetrina")],
  tone: "dark"
}, {
  icon: StickerIcon,
  title: translate("Adesivi & etichette"),
  items: [translate("Etichette prodotto"), translate("Sticker sagomati"), translate("Adesivi promozionali"), translate("QR code e barcode"), translate("Etichette numerate"), translate("Personalizzazione totale")],
  tone: "light"
}, {
  icon: QrCode,
  title: translate("Personalizzazione avanzata"),
  items: [translate("Dati variabili"), translate("Numerazioni progressive"), translate("QR code unici"), translate("Campagne marketing"), translate("Materiali per eventi"), translate("Promozioni mirate")],
  tone: "accent"
}, {
  icon: Storefront,
  title: translate("Soluzioni per aziende"),
  items: [translate("Materiale coordinato uffici"), translate("Kit promozionali fiere ed eventi"), translate("Comunicazione punti vendita"), translate("Decorazione vetrine e mezzi"), translate("Campagne stagionali"), translate("Lanci prodotto")],
  tone: "light"
}];
const REASONS = [{
  n: "01",
  title: translate("Preventivo in 24 ore"),
  body: translate("Ricevi il tuo preventivo entro 24 ore. I tempi di produzione e consegna vengono concordati in base al progetto.")
}, {
  n: "02",
  title: translate("Flessibilit\xE0"),
  body: translate("Tirature piccole, medie o grandi. Personalizzazioni totali. Adattiamo la produzione al tuo progetto.")
}, {
  n: "03",
  title: translate("Consulenza"),
  body: translate("Pratica e orientata alla soluzione. Ti aiutiamo a scegliere il prodotto, il materiale e la finitura giusta.")
}, {
  n: "04",
  title: translate("Qualit\xE0"),
  body: translate("Controllo su ogni lavorazione. Stampa svizzera, finiture professionali, attenzione al dettaglio.")
}, {
  n: "05",
  title: translate("Affidabilit\xE0"),
  body: translate("Un solo referente per tutto: grafica, stampa, personalizzazione, finitura. Partner stabile nel tempo.")
}];
export default function Landing() {
  const {
    scrollYProgress
  } = useScroll();
  const reducedMotion = useReducedMotion();
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const scrollTo = id => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({
        behavior: reducedMotion ? "auto" : "smooth",
        block: "start"
      });
      setMenuOpen(false);
    }
  };
  return <PageTransition>
      <div className="App print-v2 print-cinema" data-testid="landing-page">
        <motion.div className="reading-progress" style={{
        scaleX: scrollYProgress
      }} aria-hidden="true" />
        <Header scrolled={scrolled} onNav={scrollTo} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
        <CinematicHero onCta={() => scrollTo("contatti")} />
        <Marquee />
        <Services />
        <SeasonalIdeas onSelect={idea => {
        setSelectedIdea(idea);
        scrollTo("contatti");
      }} />
        <Story />
        <ProductionModel />
        
        <QuoteSection selectedIdea={selectedIdea} />
        <Footer onNav={scrollTo} />
      </div>
    </PageTransition>;
}

/* ------------------------------- HEADER ------------------------------- */
const LOGO_URL = "/images/pedrazzini-print365-640.webp";
function Header({
  scrolled,
  onNav,
  menuOpen,
  setMenuOpen
}) {
  return <header data-testid="site-header" data-scrolled={scrolled} className={`fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b transition-all duration-300 ${scrolled ? "border-black/10" : "border-white/30"}`}>
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 lg:px-10 h-20 lg:h-24">
        <Link to="/" onClick={() => onNav("top")} className="flex items-center gap-3" data-testid="logo-button">
          <img src={LOGO_URL} alt="Pedrazzini Print 365" width="2048" height="387" className="h-auto w-[200px] sm:w-[224px] lg:w-[228px] max-w-full object-contain select-none" draggable="false" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {NAV.map(n => n.to ? <Link key={n.id} to={n.to} className="text-[13px] font-bold tracking-[0.08em] uppercase text-[#0a0a0a] hover:text-[#1f3685] transition-colors" data-testid={`nav-${n.id}`}>
                {n.label}
              </Link> : <button key={n.id} onClick={() => onNav(n.id)} className="text-[13px] font-bold tracking-[0.08em] uppercase text-[#0a0a0a] hover:text-[#1f3685] transition-colors" data-testid={`nav-${n.id}`}>
                {n.label}
              </button>)}
        </nav>

        <div className="hidden lg:block">
          <button onClick={() => onNav("contatti")} className="inline-flex items-center gap-2 bg-[#1f3685] text-white font-bold uppercase tracking-[0.04em] text-[13px] px-6 py-3 border border-[#1f3685] hover:bg-[#162a6b] hover:border-[#162a6b] transition-colors" data-testid="header-cta">{translate("Preventivo ")}<ArrowRight size={16} weight="bold" />
          </button>
        </div>

        <button className="lg:hidden p-2 -mr-2 text-[#0a0a0a]" onClick={() => setMenuOpen(!menuOpen)} aria-label={menuOpen ? translate("Chiudi menu") : translate("Apri menu")} aria-expanded={menuOpen} data-testid="mobile-menu-toggle">
          {menuOpen ? <X size={28} weight="bold" /> : <ListIcon size={28} weight="bold" />}
        </button>
      </div>

      {menuOpen && <div className="lg:hidden bg-white border-t border-black/10" data-testid="mobile-nav">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map(n => n.to ? <Link key={n.id} to={n.to} onClick={() => setMenuOpen(false)} className="text-left text-lg font-bold tracking-tight text-[#0a0a0a]" data-testid={`mobile-nav-${n.id}`}>
                  {n.label}
                </Link> : <button key={n.id} onClick={() => onNav(n.id)} className="text-left text-lg font-bold tracking-tight text-[#0a0a0a]" data-testid={`mobile-nav-${n.id}`}>
                  {n.label}
                </button>)}
            <button onClick={() => onNav("contatti")} className="bg-[#1f3685] text-white font-bold uppercase tracking-[0.04em] text-[13px] px-6 py-3 mt-2 w-full" data-testid="mobile-cta">{translate("Richiedi preventivo")}</button>
          </div>
        </div>}
    </header>;
}

/* ------------------------------ MARQUEE ------------------------------- */
function Marquee() {
  const items = [translate("4 GENERAZIONI DI STORIA"), translate("VISIONE 365"), translate("PRODUZIONE SVIZZERA"), translate("STAMPA \xB7 PERSONALIZZAZIONE \xB7 GRANDE FORMATO"), translate("SOLUZIONI SU MISURA")];
  const repeated = [...items, ...items, ...items];
  return <section className="border-y border-black/15 bg-white overflow-hidden py-6" data-testid="marquee">
      <div className="flex marquee-track whitespace-nowrap">
        {repeated.map((t, i) => <div key={i} className="flex items-center px-8">
            <span className="font-display font-black uppercase tracking-tight text-3xl md:text-5xl text-[#d4d4d4]">
              {t}
            </span>
            <span className="mx-8 text-[#1f3685] text-4xl">✦</span>
          </div>)}
      </div>
    </section>;
}

/* -------------------------------- STORY ------------------------------- */
function Story() {
  return <section id="storia" className="brand-story" data-testid="story-section">
    <div className="story-layout">
      <div><p className="eyebrow">{translate("Le nostre radici, il tuo prossimo progetto")}</p><h2>{translate("Una storia di carta.")}<br /><span>{translate("Una visione pi\xF9 grande.")}</span></h2></div>
      <div className="story-copy"><p>{translate("Quattro generazioni di esperienza tipografica a Locarno. Dal 2025, una nuova fase con il Gruppo Deco 365.")}</p><p>{translate("Pedrazzini Print 365 unisce la tradizione della stampa alle possibilit\xE0 della personalizzazione: dalla carta alle vetrine, dai piccoli stampati alla comunicazione della tua attivit\xE0.")}</p><a href={localHref("https://deco365.ch/")} target="_blank" rel="noreferrer">{translate("Scopri il mondo Deco 365 ")}<ArrowRight size={18} /></a></div>
      <div className="story-facts"><div><strong>4</strong><span>{translate("generazioni di esperienza")}</span></div><div><strong>2025</strong><span>{translate("l\u2019ingresso nel Gruppo Deco 365")}</span></div><div><strong>Locarno</strong><span>{translate("il nostro punto di partenza")}</span></div></div>
    </div>
  </section>;
}

/* ------------------------------ SERVICES ------------------------------ */
function Services() {
  const descriptions = [translate("Dalla prima stretta di mano al catalogo che racconta la tua azienda."), translate("La tua comunicazione prende spazio. In negozio, in fiera, sul territorio."), translate("Identit\xE0 da applicare. Su prodotti, confezioni e nuove idee."), translate("Ogni copia \xE8 unica. Dati variabili, numerazioni e campagne mirate."), translate("Un unico referente. Una comunicazione che parla la stessa lingua.")];
  return <section id="servizi" className="print-services" data-testid="services-section">
    <div className="section-intro"><div><p className="eyebrow">{translate("01 / COSA REALIZZIAMO")}</p><h2>{translate("Le tue idee.")}<br /><span className="ink-accent">{translate("La nostra stampa.")}</span></h2></div><p>{translate("Dal biglietto da visita al grande formato.")}<br />{translate("La tua idea trova la sua forma.")}</p><LoopFilm src="/video/paper-detail.mp4" poster="/video/paper-detail.jpg" label="Dettaglio della lavorazione della carta" className="service-film" /></div>
    <div className="service-index">{SERVICES.map((service, i) => <motion.article key={service.title} initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: .15
      }} transition={{
        duration: .5,
        delay: i * .04
      }} className="service-row" data-testid={`service-card-${i}`}>
      <span className="service-number">0{i + 1}</span><div className="service-info"><h3>{service.title}</h3><p>{descriptions[i]}</p><details><summary>{translate("Prodotti e lavorazioni ")}<span>+</span></summary><ul>{service.items.map(item => <li key={item}>{item}</li>)}</ul></details></div><service.icon className="service-icon" size={44} weight="light" />
    </motion.article>)}</div>
  </section>;
}

/* --------------------------- PRODUCTION MODEL ------------------------- */
function ProductionModel() {
  const cols = [{
    tag: "01",
    title: translate("Produzione interna"),
    body: translate("Per lavori rapidi, ricorrenti e personalizzati. Controllo diretto su tempi e qualit\xE0."),
    items: [translate("Tempi rapidi"), translate("Tirature variabili"), translate("Personalizzazione totale"), translate("Stampa digitale Pro")]
  }, {
    tag: "02",
    title: translate("Partner specializzati"),
    body: translate("Per lavorazioni speciali e prodotti premium. Una rete di partner di fiducia."),
    items: [translate("Nobilitazioni (laminazioni, foil, UV)"), translate("Packaging strutturato"), translate("Tagli speciali"), translate("Gadget e abbigliamento personalizzato"), translate("Grandi volumi")]
  }];
  return <section id="modello" className="relative bg-[#0a0a0a] text-white py-24 lg:py-36 px-6 lg:px-10 overflow-hidden grain" data-testid="model-section">
      <div className="max-w-[1400px] mx-auto relative">
        <div className="mb-16">
          <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#1f3685] mb-4">{translate("\u2014 Modello produttivo")}</div>
          <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl md:text-5xl lg:text-6xl max-w-3xl">{translate("Un solo referente.")}<br /><span>{translate("Pi\xF9 possibilit\xE0.")}</span>
          </h2>
          <ScribbleUnderline color="#ffffff" className="w-32 opacity-80" />
          <p className="mt-6 text-white/70 max-w-2xl text-lg">{translate("Un modello flessibile per offrirti pi\xF9 soluzioni. Un solo referente, pi\xF9 possibilit\xE0, qualit\xE0 controllata su ogni lavorazione.")}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-px bg-white/20 border border-white/20">
          {cols.map(c => <div key={c.tag} className="bg-[#0a0a0a] p-8 lg:p-12">
              <div className="flex items-center gap-4 mb-6">
                <span className="font-display font-black text-5xl text-[#1f3685] tracking-tighter">{c.tag}</span>
                <h3 className="font-display font-black uppercase tracking-tight text-2xl lg:text-3xl">{c.title}</h3>
              </div>
              <p className="text-white/70 mb-6 text-base lg:text-lg">{c.body}</p>
              <ul className="space-y-3 border-t border-white/15 pt-6">
                {c.items.map(it => <li key={it} className="flex items-center gap-3 text-sm">
                    <span className="w-1.5 h-1.5 bg-[#1f3685]" />
                    <span>{it}</span>
                  </li>)}
              </ul>
            </div>)}
        </div>

        <div className="mt-12 inline-flex items-center gap-3 border border-white/30 px-6 py-4">
          <Handshake size={22} weight="duotone" className="text-[#1f3685]" />
          <span className="text-sm">
            <strong>{translate("Un solo referente")}</strong>{translate(" \xB7 pi\xF9 possibilit\xE0 \xB7 qualit\xE0 controllata.")}</span>
        </div>
      </div>
    </section>;
}

/* -------------------------------- WHY US ------------------------------ */
function WhyUs() {
  const icons = [Lightning, Sparkle, Handshake, ShieldCheck, ShieldCheck];
  return <section id="perche" className="py-24 lg:py-36 px-6 lg:px-10" data-testid="why-section">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-16 max-w-3xl">
          <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#1f3685] mb-4">{translate("\u2014 Perch\xE9 sceglierci")}</div>
          <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl md:text-5xl lg:text-6xl">
            <WordReveal text="Cinque ragioni" /><br />
            <span className="text-stroke"><WordReveal text="per" delay={0.15} /></span> <span className="text-[#1f3685]"><WordReveal text="fidarsi." delay={0.25} /></span>
          </h2>
          <ScribbleUnderline className="w-32" />
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/15 border border-black/15">
          {REASONS.map((r, i) => {
          const Icon = icons[i] || ShieldCheck;
          return <motion.div key={r.n} initial={{
            opacity: 0,
            y: 18
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.45,
            delay: i * 0.05
          }} className="bg-white p-8 lg:p-10" data-testid={`reason-${i}`}>
                <div className="flex items-start justify-between mb-8">
                  <span className="font-display font-black text-6xl text-[#1f3685] tracking-tighter leading-none">{r.n}</span>
                  <Icon size={32} weight="duotone" className="text-[#0a0a0a]" />
                </div>
                <h3 className="font-display font-black uppercase tracking-tight text-2xl mb-3">{r.title}</h3>
                <p className="text-[#0a0a0a]/70 leading-relaxed">{r.body}</p>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}

/* ------------------------------- QUOTE FORM --------------------------- */
export function QuoteSection({
  selectedIdea
}) {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service: translate("Stampati aziendali"),
    message: ""
  });
  useEffect(() => {
    if (!selectedIdea) return;
    const interest = translate("Mi interessa: {0} (rubrica {1}).", {
      0: selectedIdea.title,
      1: selectedIdea.edition
    });
    setForm(previous => ({
      ...previous,
      service: selectedIdea.service,
      message: previous.message.includes(interest) ? previous.message : [previous.message, interest].filter(Boolean).join("\n\n")
    }));
  }, [selectedIdea]);
  const [submitting, setSubmitting] = useState(false);
  const handle = k => e => setForm({
    ...form,
    [k]: e.target.value
  });
  const submit = async e => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(translate("Compila i campi obbligatori."));
      return;
    }
    if (!BACKEND_URL) {
      const subject = translate("Richiesta preventivo \xB7 {0}", {
        0: form.service
      });
      const body = translate("Nome: {0}\nAzienda: {1}\nEmail: {2}\nTelefono: {3}\nServizio: {4}\n\n{5}", {
        0: form.name,
        1: form.company,
        2: form.email,
        3: form.phone,
        4: form.service,
        5: form.message
      });
      window.location.href = `mailto:info@pedrazziniprint365.ch?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      toast.info(translate("Richiesta preparata: completa l\u2019invio nella tua app di posta."));
      return;
    }
    setSubmitting(true);
    try {
      const {
        default: axios
      } = await import("axios");
      const res = await axios.post(`${API}/quote`, form);
      if (res.data?.email_sent) {
        toast.success(translate("Richiesta inviata. Ti ricontattiamo a breve."));
      } else {
        toast.success(translate("Richiesta ricevuta. Ti ricontattiamo a breve."));
      }
      setForm({
        name: "",
        company: "",
        email: "",
        phone: "",
        service: translate("Stampati aziendali"),
        message: ""
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || translate("Errore nell'invio. Riprova.");
      toast.error(typeof msg === "string" ? msg : translate("Errore nell'invio."));
    } finally {
      setSubmitting(false);
    }
  };
  return <section id="contatti" className="py-24 lg:py-36 px-6 lg:px-10 bg-[#f4f4f2] border-y border-black/10" data-testid="quote-section">
      <div className="ph-contact-grid max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12">
        <div className="ph-contact-blue lg:col-span-5">
          <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#1f3685] mb-4">{translate("\u2014 Contattaci")}</div>
          <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl md:text-5xl lg:text-6xl">
            <WordReveal text={translate("Hai un progetto?")} /><br />
            <span className="text-[#1f3685]"><WordReveal text={translate("Parliamone.")} delay={0.2} /></span>
          </h2>
          <ScribbleUnderline className="w-32" />
          <p className="mt-6 text-[#0a0a0a]/75 text-lg max-w-md">{translate("Richiedi un preventivo o una consulenza. Ti aiutiamo a trovare la soluzione pi\xF9 adatta, anche per lavorazioni speciali.")}</p>

          <div className="mt-10 space-y-5">
            <ContactRow icon={MapPin} label={translate("Sede")} value="Via Varenna 7, 6600 Locarno · CH" />
            <ContactRow icon={Phone} label={translate("Telefono")} value="+41 91 751 77 34" href={localHref("tel:+41917517734")} testid="contact-phone" />
            <ContactRow icon={EnvelopeSimple} label={translate("Email")} value="info@pedrazziniprint365.ch" href={localHref("mailto:info@pedrazziniprint365.ch")} testid="contact-email" />
          </div>

          <div className="ph-contact-promise mt-10 border border-black/20 p-6 bg-white">
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#737373] mb-2">{translate("Il tuo preventivo")}</div>
            <div className="font-display font-black text-3xl tracking-tight">{translate("Entro 24 ore.")}</div>
          </div>
        </div>

        <div className="ph-contact-form-panel lg:col-span-7">
          <form onSubmit={submit} className="bg-white border border-black p-6 lg:p-10 space-y-5" data-testid="quote-form"><div className="ph-form-intro"><span className="ph-label">{translate("DAL PENSIERO ALLA CARTA")}</span><h3>{translate("Cominciamo da te.")}</h3><p>{translate("Bastano pochi dettagli per partire.")}</p></div>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label={translate("Nome e cognome *")}>
                <input className="brut-input" value={form.name} onChange={handle("name")} required data-testid="form-name" />
              </Field>
              <Field label={translate("Azienda")}>
                <input className="brut-input" value={form.company} onChange={handle("company")} data-testid="form-company" />
              </Field>
              <Field label={translate("Email *")}>
                <input type="email" className="brut-input" value={form.email} onChange={handle("email")} required data-testid="form-email" />
              </Field>
              <Field label={translate("Telefono")}>
                <input className="brut-input" value={form.phone} onChange={handle("phone")} data-testid="form-phone" />
              </Field>
            </div>

            <Field label={translate("Servizio richiesto *")}>
              <select className="brut-input" value={form.service} onChange={handle("service")} data-testid="form-service">
                {SERVICES.map(s => <option key={s.title}>{s.title}</option>)}
                <option>{translate("Stampe per privati")}</option>
                <option>{translate("Altro / Non sono sicuro")}</option>
              </select>
            </Field>

            <Field label={translate("Descrivi il tuo progetto *")}>
              <textarea className="brut-input min-h-[140px] resize-y" value={form.message} onChange={handle("message")} required placeholder={translate("Cosa vuoi realizzare? Indica quantit\xE0, utilizzo e data in cui ti serve. Se non sai quali materiali scegliere, ti aiutiamo noi.")} data-testid="form-message" />
            </Field>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
              <p className="text-xs text-[#737373] max-w-sm">{translate("I dati saranno utilizzati per rispondere alla tua richiesta.")}</p>
              {!BACKEND_URL && <p className="text-sm mb-4">{translate("Il pulsante apre la tua app di posta con la richiesta compilata.")}</p>}
              <button type="submit" disabled={submitting} className="brut-btn accent disabled:opacity-60 disabled:cursor-not-allowed" data-testid="quote-submit">
                {submitting ? translate("Invio...") : BACKEND_URL ? translate("Invia richiesta") : translate("Prepara email")} <PaperPlaneTilt size={16} weight="bold" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>;
}
function Field({
  label,
  children
}) {
  return <label className="block">
      <span className="block text-[10px] tracking-[0.22em] font-bold uppercase text-[#737373] mb-2">{label}</span>
      {children}
    </label>;
}
function ContactRow({
  icon: Icon,
  label,
  value,
  href,
  testid
}) {
  const inner = <div className="flex items-start gap-4">
      <div className="w-10 h-10 border border-black/20 grid place-items-center bg-white">
        <Icon size={18} weight="bold" />
      </div>
      <div>
        <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#737373] mb-1">{label}</div>
        <div className="font-display font-bold text-lg tracking-tight">{value}</div>
      </div>
    </div>;
  if (href) {
    return <a href={localHref(href)} className="block hover:text-[#1f3685] transition-colors" data-testid={testid}>
        {inner}
      </a>;
  }
  return <div data-testid={testid}>{inner}</div>;
}

/* -------------------------------- FOOTER ------------------------------ */
function Footer({
  onNav
}) {
  return <footer className="bg-[#0a0a0a] text-white pt-20 pb-10 px-6 lg:px-10" data-testid="footer">
      <div className="max-w-[1400px] mx-auto">
        <h2 className="font-display font-black uppercase tracking-tighter leading-[0.85] text-[14vw] lg:text-[10vw] text-stroke-white select-none break-all">
          PEDRAZZINI<br />
          <span className="text-white">PRINT 365</span>
        </h2>

        <div className="mt-16 grid md:grid-cols-4 gap-10 border-t border-white/15 pt-10">
          <div className="md:col-span-2">
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#1f3685] mb-3">{translate("Sede")}</div>
            <p className="text-white/80 leading-relaxed">
              Tipografia Pedrazzini Print 365 Sagl<br />
              Via Varenna 7, 6600 Locarno · CH
            </p>
            <p className="mt-4 text-white/60 text-sm">{translate("Parte del Gruppo Deco 365 Sagl")}</p>
          </div>

          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#1f3685] mb-3">{translate("Naviga")}</div>
            <ul className="space-y-2">
              {NAV.map(n => n.to ? <li key={n.id}>
                    <Link to={n.to} className="text-white/80 hover:text-white text-sm" data-testid={`footer-nav-${n.id}`}>
                      {n.label}
                    </Link>
                  </li> : <li key={n.id}>
                    <button onClick={() => onNav(n.id)} className="text-white/80 hover:text-white text-sm" data-testid={`footer-nav-${n.id}`}>
                      {n.label}
                    </button>
                  </li>)}
            </ul>
          </div>

          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#1f3685] mb-3">{translate("Contatti")}</div>
            <ul className="space-y-2 text-white/80 text-sm">
              <li><a href={localHref("tel:+41917517734")} className="hover:text-white" data-testid="footer-phone">+41 91 751 77 34</a></li>
              <li><a href={localHref("mailto:info@pedrazziniprint365.ch")} className="hover:text-white" data-testid="footer-email">info@pedrazziniprint365.ch</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-white/15 flex flex-col md:flex-row justify-between gap-4 text-xs text-white/50">
          <p>© {new Date().getFullYear()}{translate(" Tipografia Pedrazzini Print 365 Sagl \xB7 Tutti i diritti riservati")}</p>
          <p className="tracking-[0.22em] uppercase font-bold">{translate("Stampa svizzera \xB7 Locarno")}</p>
          <p className="footage-credit">{translate("Video di repertorio: ")}<a href={localHref("https://www.vidsplay.com/")} target="_blank" rel="noreferrer">Vidsplay.com</a></p>
        </div>
      </div>
    </footer>;
}
